import dayjs from 'dayjs';
import {z} from 'zod';

import i18n from '@/i18n';
import {TIME_FORMATS} from '@/utils/timeFormats';

import {EXPORT_RESIDENT_PARAM} from './exportParams';

export const noOptions = {
  optionsSchema: z.object({}),
  getDefaultOptions: () => ({}),
};

export const dateRangeOptionsSchema = z
  .object({
    startDate: z.string().min(1, i18n.t('Date range is required')),
    endDate: z.string().min(1, i18n.t('Date range is required')),
  })
  .refine(({startDate, endDate}) => !dayjs(endDate).isBefore(startDate), {
    message: i18n.t('End date cannot be before start date'),
    path: ['startDate'],
  })
  .refine(
    ({startDate, endDate}) =>
      !dayjs(endDate).isAfter(dayjs(startDate).add(1, 'year'), 'day'),
    {
      message: i18n.t('Date range cannot be longer than one year'),
      path: ['startDate'],
    }
  );

export type DateRangeOptions = z.infer<typeof dateRangeOptionsSchema>;

const getValidDate = (value: string | null) =>
  value && dayjs(value).isValid() ? value : undefined;

export const getDateRangeDefaultOptions = (
  searchParams: URLSearchParams
): DateRangeOptions => {
  const startDate =
    getValidDate(searchParams.get('start')) ??
    dayjs().format(TIME_FORMATS.awsDate);

  return {
    startDate,
    endDate: getValidDate(searchParams.get('end')) ?? startDate,
  };
};

export const residentOptionsSchema = z.object({
  residentId: z.string().min(1, i18n.t('Resident is required')),
});

export type ResidentOptions = z.infer<typeof residentOptionsSchema>;

export const getResidentDefaultOptions = (
  searchParams: URLSearchParams
): ResidentOptions => ({
  residentId: searchParams.get(EXPORT_RESIDENT_PARAM) ?? '',
});

export const residentDateRangeOptionsSchema = residentOptionsSchema.and(
  dateRangeOptionsSchema
);

export type ResidentDateRangeOptions = z.infer<
  typeof residentDateRangeOptionsSchema
>;

export const getResidentDateRangeDefaultOptions = (
  searchParams: URLSearchParams
): ResidentDateRangeOptions => ({
  ...getResidentDefaultOptions(searchParams),
  startDate:
    getValidDate(searchParams.get('start')) ??
    dayjs().subtract(29, 'day').format(TIME_FORMATS.awsDate),
  endDate:
    getValidDate(searchParams.get('end')) ??
    dayjs().format(TIME_FORMATS.awsDate),
});

export const getDateRangeFileName = (
  name: string,
  {startDate, endDate}: DateRangeOptions
) => `${name}-${startDate}-${endDate}`;

export const describeDateRange = ({startDate, endDate}: DateRangeOptions) =>
  startDate === endDate
    ? formatExportDate(startDate)
    : `${formatExportDate(startDate)} – ${formatExportDate(endDate)}`;

export const isWithinDateRange = (
  date: string,
  {startDate, endDate}: DateRangeOptions
) =>
  !dayjs(date).isBefore(startDate, 'day') &&
  !dayjs(date).isAfter(endDate, 'day');

export const formatExportDate = (date?: string | null) =>
  date ? dayjs(date).format(TIME_FORMATS.date) : '';

export const formatExportDateTime = (date?: string | null) =>
  date ? dayjs(date).format(TIME_FORMATS.dateTime) : '';

export const formatPersonName = (name?: {
  firstName: string;
  lastName: string;
}) => (name ? `${name.firstName} ${name.lastName}` : '');
